const THEME_KEY = "theme";

export type ThemeMode = "light" | "dark";

const isBrowser = typeof window !== "undefined";

/** Time-locked showroom theme: dark from 19:00 through 06:59 in the user's IANA timezone. */
export const getSystemTheme = (): ThemeMode => {
  if (!isBrowser) return "dark";
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    const hourStr = new Intl.DateTimeFormat("en-US", {
      hour: "numeric", hour12: false, timeZone: tz,
    }).format(new Date());
    const h = Number.parseInt(hourStr, 10);
    if (Number.isFinite(h)) return (h >= 19 || h < 7) ? "dark" : "light";
    const fallback = new Date().getHours();
    return (fallback >= 19 || fallback < 7) ? "dark" : "light";
  } catch {
    return "dark";
  }
};

export const getStoredTheme = (): ThemeMode | null => {
  if (!isBrowser) return null;
  const stored = window.localStorage.getItem(THEME_KEY);
  return stored === "dark" || stored === "light" ? stored : null;
};

export const resolveInitialTheme = (): ThemeMode => getStoredTheme() ?? getSystemTheme();

export const applyTheme = (theme: ThemeMode) => {
  if (!isBrowser) return;
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.documentElement.classList.toggle("light", theme === "light");
  document.documentElement.style.colorScheme = theme;
  document.documentElement.dataset.theme = theme;
};

export const persistTheme = (theme: ThemeMode) => {
  if (!isBrowser) return;
  window.localStorage.setItem(THEME_KEY, theme);
};

export const clearStoredTheme = () => {
  if (!isBrowser) return;
  window.localStorage.removeItem(THEME_KEY);
};

export const initializeTheme = () => {
  const theme = resolveInitialTheme();
  applyTheme(theme);
  installSystemThemeWatcher();
  return theme;
};

export const getDocumentTheme = (): ThemeMode => {
  if (!isBrowser) return "dark";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
};

export const ensureThemeSynchronization = () => {
  if (!isBrowser) return;
  const expected = resolveInitialTheme();
  if (getDocumentTheme() !== expected || document.documentElement.style.colorScheme !== expected) {
    applyTheme(expected);
  }
  document.documentElement.dataset.theme = expected;
};

/* ── Real-time OS scheme watcher ──────────────────────────────────
 * Hooks into `matchMedia('(prefers-color-scheme: dark)')`. When the
 * OS flips (e.g., automatic sunset switch) and the user has not
 * locked a manual preference, the DOM updates without a reload.
 * Manual choices in localStorage always win. */
type SystemThemeListener = (mode: ThemeMode) => void;
const systemThemeListeners = new Set<SystemThemeListener>();
let systemThemeWatcherInstalled = false;

export const installSystemThemeWatcher = () => {
  if (!isBrowser || systemThemeWatcherInstalled) return;
  systemThemeWatcherInstalled = true;
  const handler = () => {
    // Manual preference always wins — never override a stored choice.
    if (getStoredTheme() !== null) return;
    const next: ThemeMode = getSystemTheme();
    systemThemeListeners.forEach((cb) => cb(next));
    applyTheme(next);
  };
  window.addEventListener("focus", handler, { passive: true });
  document.addEventListener("visibilitychange", handler);
};

export const subscribeSystemTheme = (cb: SystemThemeListener): (() => void) => {
  installSystemThemeWatcher();
  systemThemeListeners.add(cb);
  return () => systemThemeListeners.delete(cb);
};

